// CostsReportTable.js
import React, { useState } from "react";
import { Table, Collapse, Button } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const CostsReportTable = ({ locations }) => {
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const itemsPerPage = 5;

    const toggleExpand = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const paginate = (arr, key) => {
        const currentPage = page[key] || 1;
        const start = (currentPage - 1) * itemsPerPage;
        return arr.slice(start, start + itemsPerPage);
    };

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / itemsPerPage));

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Amount</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter((item) => item.costs.length > 0)
                        .map((loc) => {
                            const totalAmount = loc.costs.reduce(
                                (acc, c) => acc + (c.amount || 0),
                                0
                            );
                            const key = `loc-${loc.id}`;

                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>${formatCurrency(totalAmount)}</td>
                                        <td>
                                            <Button
                                                className="button-gradient-bg"
                                                onClick={() => toggleExpand(key)}
                                            >
                                                {expanded[key] ? "Hide Costs" : "View Costs"}
                                            </Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={3} className="p-0 border-0">
                                            <Collapse isOpen={expanded[key]}>
                                                <Table
                                                    bordered
                                                    size="sm"
                                                    responsive
                                                    className="mb-0 table-striped"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Type</th>
                                                            <th>Amount</th>
                                                            <th>Note</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginate(loc.costs, key).map((c) => (
                                                            <tr key={c.id}>
                                                                <td>{formatDate(c.payment_date)}</td>
                                                                <td>{c.type}</td>
                                                                <td>${formatCurrency(c.amount)}</td>
                                                                <td>{c.note || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {loc.costs.length > itemsPerPage && (
                                                    <div className="d-flex justify-content-between align-items-center p-2">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.max(
                                                                        1,
                                                                        (page[key] || 1) - 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === 1}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <span>
                                                            Page {page[key] || 1} of{" "}
                                                            {totalPages(loc.costs)}
                                                        </span>
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.min(
                                                                        totalPages(loc.costs),
                                                                        (page[key] || 1) + 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={
                                                                (page[key] || 1) ===
                                                                totalPages(loc.costs)
                                                            }
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

CostsReportTable.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            costs: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    payment_date: PropTypes.string.isRequired,
                    type: PropTypes.string.isRequired,
                    amount: PropTypes.number.isRequired,
                    note: PropTypes.string,
                })
            ),
        })
    ).isRequired,
};

export default CostsReportTable;